import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { ISftpPartner } from '@core/models/sftp-transfer.model';
import { getSftpTransfersThunk } from '@redux/features/sftp-integration.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DebouncedInput from 'Shared/DebouncedInput';
import FilterInput from 'Shared/FilterInput';
import sftpIntegrationService from 'services/api/sftp-integration.service';
import {
    canPollPartner,
    directionBadgeClass,
    directionLabel,
    sortPartnersForDisplay,
} from './sftp-partner.utils';
import { pollToastMessage } from './sftp-user-messages';
import SftpStatusGuide from './SftpStatusGuide';
import SftpEpcisDispatchPanel from './SftpEpcisDispatchPanel';
import {
    formatStatusLabel,
    isSftpTransferClosed,
    sftpStatusBadgeClass,
} from './sftp-transfer-status.utils';

const STATUS_OPTIONS = [
    { value: 'ALL', label: 'All' },
    { value: 'AWAITING_ADMIN_INPUT', label: 'Awaiting admin input' },
    { value: 'VALIDATION_FAILED', label: 'Validation failed' },
    { value: 'ADMIN_REJECTED', label: 'Admin rejected' },
    { value: 'PROCESSED', label: 'Processed' },
];

const DIRECTION_OPTIONS = [
    { value: 'ALL', label: 'All directions' },
    { value: 'RECEIVE', label: 'Inbound (receive)' },
    { value: 'OUTBOUND', label: 'Outbound (send)' },
];

const SftpIntegrations: FC = () => {
    const [page, setPage] = useState(1);
    const [statusTab, setStatusTab] = useState('ALL');
    const [directionTab, setDirectionTab] = useState('ALL');
    const [partnerCode, setPartnerCode] = useState('');
    const [partners, setPartners] = useState<ISftpPartner[]>([]);
    const [pollingCode, setPollingCode] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const { transfers, loading } = useAppSelector((state: RootState) => state.sftpIntegrationState);

    const changePage = (p: string) => setPage(Number(p));

    const refreshTransfers = useCallback(() => {
        dispatch(
            getSftpTransfersThunk({
                page: page - 1,
                size: 10,
                status: statusTab === 'ALL' ? undefined : statusTab,
                partnerCode: partnerCode || undefined,
                transferDirection: directionTab === 'ALL' ? undefined : directionTab,
            })
        );
    }, [dispatch, page, statusTab, directionTab, partnerCode]);

    useEffect(() => {
        refreshTransfers();
    }, [refreshTransfers]);

    const loadPartners = useCallback(async () => {
        try {
            const res = await sftpIntegrationService.listIntegrations();
            setPartners(res.data.integrations || []);
        } catch {
            /* interceptor */
        }
    }, []);

    useEffect(() => {
        loadPartners();
    }, [loadPartners]);

    const pollPartner = async (code: string) => {
        setPollingCode(code);
        try {
            const res = await sftpIntegrationService.poll(code);
            toast.success(pollToastMessage(res.data?.message, code));
            refreshTransfers();
        } catch {
            /* toast from interceptor */
        } finally {
            setPollingCode(null);
        }
    };

    const pollAllReceive = async () => {
        const receivePartners = sortPartnersForDisplay(partners).filter(
            (p) => p.enabled !== false && canPollPartner(p.direction) && p.code
        );
        if (receivePartners.length === 0) {
            toast.info('No enabled RECEIVE or BIDIRECTIONAL partners to download from');
            return;
        }
        setPollingCode('ALL');
        try {
            for (const p of receivePartners) {
                const res = await sftpIntegrationService.poll(p.code!);
                toast.success(`${p.code}: ${pollToastMessage(res.data?.message, p.code)}`);
            }
            refreshTransfers();
        } catch {
            /* interceptor */
        } finally {
            setPollingCode(null);
        }
    };

    const sortedPartners = sortPartnersForDisplay(partners);
    const receivePollable = sortedPartners.filter((p) => p.enabled !== false && canPollPartner(p.direction));
    const rows = transfers?.content || [];
    const isPolling = pollingCode !== null;

    return (
        <>
            <BreadCrumb pageTitle="SFTP Integrations" />
            <section className="py-5">
                <div className="flex min-w-0 flex-col gap-3 bg-[#fff] p-3">
                    <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-3">
                        <h3 className="font-extrabold text-[#bd902e]">SFTP Transfers</h3>
                        <div className="flex flex-wrap gap-2">
                            <Link
                                to={PRIVATEPATHS.SftpPartners}
                                className="inline-flex items-center rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                                Partners
                            </Link>
                            {receivePollable.length > 1 && (
                                <ButtonBase
                                    innerText={pollingCode === 'ALL' ? 'Downloading…' : 'Download all receive'}
                                    disabled={isPolling}
                                    onClick={pollAllReceive}
                                />
                            )}
                        </div>
                    </div>

                    <SftpStatusGuide />

                    <SftpEpcisDispatchPanel />

                    {sortedPartners.length > 0 && (
                        <div className="w-full min-w-0 rounded-md border border-slate-200 bg-slate-50 p-3">
                            <p className="mb-2 text-sm font-medium text-slate-700">Partners</p>
                            <div className="-mx-1 flex snap-x snap-mandatory flex-nowrap gap-2 overflow-x-auto px-1 pb-2">
                                {sortedPartners.map((p) => {
                                    const dir = directionLabel(p.direction);
                                    const pollable = canPollPartner(p.direction);
                                    const disabled = p.enabled === false;
                                    return (
                                        <div
                                            key={p.code}
                                            className="inline-flex shrink-0 snap-start items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-sm"
                                        >
                                            <span className="font-semibold text-slate-800">{p.code}</span>
                                            <span
                                                className={`rounded px-1.5 py-0.5 text-xs font-medium ${directionBadgeClass(p.direction)}`}
                                            >
                                                {dir}
                                            </span>
                                            {disabled && <span className="text-xs text-slate-500">disabled</span>}
                                            {pollable && !disabled ? (
                                                <button
                                                    type="button"
                                                    className="text-xs font-semibold text-[#bd902e] hover:underline disabled:opacity-50"
                                                    disabled={isPolling}
                                                    title="Fetch new files from SFTP"
                                                    onClick={() => pollPartner(p.code!)}
                                                >
                                                    {pollingCode === p.code ? 'Downloading…' : 'Download files'}
                                                </button>
                                            ) : (
                                                <span className="text-xs text-slate-400" title="Outbound only — no inbound download">
                                                    Send only
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-4">
                        <div className="flex flex-wrap gap-5">
                            <div className="min-w-[200px] flex-1">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Status</label>
                                <FilterInput
                                    value={statusTab}
                                    onChange={(v) => {
                                        setStatusTab(v);
                                        setPage(1);
                                    }}
                                    options={STATUS_OPTIONS}
                                    id="sftp-status"
                                    placeholder="All"
                                    optionLabelKey="label"
                                    optionValueKey="value"
                                />
                            </div>
                            <div className="min-w-[200px] flex-1">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Direction</label>
                                <FilterInput
                                    value={directionTab}
                                    onChange={(v) => {
                                        setDirectionTab(v);
                                        setPage(1);
                                    }}
                                    options={DIRECTION_OPTIONS}
                                    id="sftp-direction"
                                    placeholder="All directions"
                                    optionLabelKey="label"
                                    optionValueKey="value"
                                />
                            </div>
                            <div className="min-w-[200px] flex-1">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Partner code</label>
                                <DebouncedInput
                                    placeholder="Search partner code"
                                    searchValue={partnerCode}
                                    searchFn={(v) => {
                                        setPartnerCode(v);
                                        setPage(1);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <AnimatedListSkeleton rows={6} />
                    ) : (
                        <>
                            <div className="w-full min-w-0 overflow-x-auto">
                                <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                    <thead className="ltr:text-left rtl:text-right">
                                        <tr className="relative rounded-md bg-slate-50">
                                            <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                            <th className="px-3.5 py-2.5 font-semibold uppercase">Partner</th>
                                            <th className="px-3.5 py-2.5 font-semibold uppercase">File</th>
                                            <th className="px-3.5 py-2.5 font-semibold uppercase">Status</th>
                                            <th className="px-3.5 py-2.5 font-semibold uppercase">Schema</th>
                                            <th className="px-3.5 py-2.5 font-semibold uppercase">Created</th>
                                            <th className="px-3.5 py-2.5 font-semibold uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <AnimatedTableBody
                                        loading={loading}
                                        itemCount={rows.length}
                                        animationKey={`sftp-${page}-${statusTab}-${directionTab}-${partnerCode}`}
                                    >
                                        {rows.map((row: any) => (
                                            <AnimatedTableRow
                                                key={row.id}
                                                rowKey={row.id}
                                                detailRecord={row}
                                                className="relative rounded-md bg-slate-50 hover:bg-[#bd902e10]"
                                            >
                                                <td className="px-3.5 py-2.5">{row.id}</td>
                                                <td className="px-3.5 py-2.5">{row.partnerCode || '—'}</td>
                                                <td className="max-w-[200px] truncate px-3.5 py-2.5">
                                                    {row.remoteFileName || '—'}
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    <span
                                                        className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${sftpStatusBadgeClass(row.status)}`}
                                                    >
                                                        {formatStatusLabel(row.status)}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    {row.schemaValid === true && (
                                                        <span className="text-green-700">Valid</span>
                                                    )}
                                                    {row.schemaValid === false && (
                                                        <span className="text-red-600">Invalid</span>
                                                    )}
                                                    {row.schemaValid == null && '—'}
                                                </td>
                                                <td className="px-3.5 py-2.5">{row.createdAt || '—'}</td>
                                                <td className="px-3.5 py-2.5">
                                                    {isSftpTransferClosed(row.status, row.approvalStatus) ? (
                                                        <span
                                                            className="text-sm text-slate-400"
                                                            title="This transfer is closed — no further admin actions"
                                                        >
                                                            —
                                                        </span>
                                                    ) : (
                                                        <Link
                                                            to={`${PRIVATEPATHS.SftpIntegrations}/${row.id}`}
                                                            className="font-semibold text-[#bd902e] hover:underline"
                                                        >
                                                            Detail
                                                        </Link>
                                                    )}
                                                </td>
                                            </AnimatedTableRow>
                                        ))}
                                    </AnimatedTableBody>
                                </AnimatedTable>
                            </div>
                            <Pagination
                                perPageData={transfers?.pageable?.pageSize || 10}
                                total={transfers?.totalElements || 0}
                                currentPage={page}
                                changePage={changePage}
                                length={transfers?.numberOfElements || rows.length}
                            />
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default SftpIntegrations;
